import React, { useEffect, useMemo, useState } from 'react';
import { Col } from 'react-bootstrap';
import { FaTrash, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';

import SubAbas from './subabas';
import BarraInferiorBotoes from '../../../components/BarraInferiorBotoes';
import BarraSubAbas from '../../../components/BarraSubAbas';
import BoxConteudo from '../../../components/BoxConteudo';
import ContainerConteudo from '../../../components/ContainerConteudo';
import CustomCol from '../../../components/CustomCol';
import CustomRow from '../../../components/CustomRow';
import Input from '../../../components/InputNew';
import Select from '../../../components/InputNew/Select';
import SelectInput from '../../../components/InputNew/Select';
import Log from '../../../components/Log/Log';
import Modal from '../../../components/Modal';
import Status from '../../../components/Status';
import Tabela from '../../../components/Tabela';
import WorkspaceBotoes from '../../../components/WorkspaceBotoes';
import WorkspaceBotoesNew from '../../../components/WorkspaceBotoes/indexNew';
import WorkspaceFicha from '../../../components/WorkspaceFicha';
import { getUser } from '../../../modules/contexts/Auth/RequireAuth';
import { usePasswordValidation } from '../../../modules/hooks/usePasswordValidation';
import { useCor } from '../../../services/enum/useCorPele';
import { useGeneros } from '../../../services/enum/useGeneros';
import httpClient from '../../../services/httpClient';
import { getUserData } from '../../../services/userService';
import { useUserIdData } from '../../../services/usuario/useUserIdData';
import { formataNomeMeioSiglas, formatDate } from '../../../utils/functions';
import { swal, swalAtualizacao } from '../../../utils/swal';

const MeusDadosUsuario: React.FC = () => {
  const { formData, setFormData } = useUserIdData();
  const [showModal, setShowModal] = useState<boolean>(false);

  const [senha, setSenha] = useState<string>('');
  const [confirmaSenha, setConfirmaSenha] = useState<string>('');

  const user = getUser();
  const userID = user.sub;

  const [validLength, passwordMatch] = usePasswordValidation({
    firstPassword: senha,
    secondPassword: confirmaSenha,
  });
  const [passwordDigits, setPasswordDigits] = useState<number>(0);

  const generos = useGeneros();
  const cores = useCor();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await getUserData(userID);
        setFormData(userData);
      } catch (error) {
        console.error('Erro ao buscar dados do usuário:', error);
        swal('error', 'Erro', 'Erro ao carregar dados do usuário.');
      }
    };
    if (userID) fetchUserData();
  }, [userID, setFormData]);

  const openSenhaModal = () => setShowModal(true);
  const closeSenhaModal = () => setShowModal(false);

  const handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
      | { name: string; value: any },
  ) => {
    const { name, value } = 'target' in event ? event.target : event;
    const enumFields = [
      'genero',
      'cor',
      'tipoVinculo',
      'orgaoOrigem',
      'unidadeDAF',
    ];
    const newValue = enumFields.includes(name)
      ? parseInt(value as string, 10)
      : value;

    setFormData((prev: any) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSenhaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSenha(e.target.value);
    setPasswordDigits(e.target.value.length);
  };

  const handleConfirmaSenhaChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setConfirmaSenha(e.target.value);
  };

  const validaSenhaTamanho = () => {
    if (passwordDigits > 0 && !validLength) {
      swal(
        'error',
        'Senha inválida',
        'A senha deve conter no mínimo 6 caracteres!',
      );
      setSenha('');
    }
  };

  const validaSenhaConfirma = () => {
    if (passwordDigits >= 6 && !passwordMatch) {
      swal('error', 'Senha inválida', 'As senhas digitadas não são iguais!');
      setConfirmaSenha('');
    }
  };

  const updateData = async () => {
    try {
      const response = await httpClient.patch(`/api/users/${userID}`, formData);
      swalAtualizacao('Sucesso', response.data.message);
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
      swal('error', 'Erro', 'Alterações não foram realizadas com sucesso.');
    }
  };

  const updateSenha = async () => {
    try {
      if (!validLength || !passwordMatch) {
        throw new Error(
          'As senhas devem ter no mínimo 6 caracteres e serem iguais.',
        );
      }
      const response = await httpClient.patch(`/api/users/${userID}/senha`, {
        novaSenha: senha,
      });
      if (response.status === 200) {
        setShowModal(false);
        setSenha('');
        setConfirmaSenha('');
        swalAtualizacao('Sucesso', response.data.message);
      }
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      swal(
        'error',
        'Erro ao alterar senha',
        'As senhas devem ter no mínimo 6 caracteres e serem iguais.',
      );
    }
  };

  interface Ses {
    id?: number;
    nome: string;
    cnes: string;
    nomeEmpresarial: string;
  }

  interface VinculoSes {
    id: string | number;
    statusInterface: string;
    tpVinculo: number;
    ses: Ses;
    sesDetalhado?: Ses;
    dataVinculacao: string | null;
    usuarioVinculacao: { nomeCompleto?: string } | null;
    observacao: string;
    stRegistroAtivo?: string;
  }

  const [showDetalhesSes, setShowDetalhesSes] = useState(false);
  const [sesSelecionada, setSesSelecionada] = useState<VinculoSes | null>(null);
  const [, setDesvinculando] = useState(false);
  const [sesVinculadas, setSesVinculadas] = useState<VinculoSes[]>([]);

  const formatarCNES = (cnes?: string) => {
    const s = (cnes ?? '').toString().replace(/\D/g, '');
    return s ? s.padStart(7, '0') : '---';
  };

  const getSesIdFromRelacao = (v: any): number | null =>
    v?.ses?.id ?? v?.idSes ?? v?.coSes ?? null;

  const getSesDetalhes = async (id: number): Promise<Ses> => {
    try {
      const { data } = await httpClient.get(`/api/ses/${id}`);
      return {
        id: data?.id,
        nome: data?.nome ?? '---',
        cnes: data?.cnes ?? '---',
        nomeEmpresarial: data?.nomeEmpresarial ?? '---',
      };
    } catch {
      return { id, nome: '---', cnes: '---', nomeEmpresarial: '---' };
    }
  };

  const carregarVinculosAtivos = async () => {
    try {
      const { data } = await httpClient.get(`/api/usuariointerface`, {
        params: { coUsuario: userID, stRegistroAtivo: 'S' },
      });

      const vinculos = data.relacoes || [];

      const relacoesCompletas: VinculoSes[] = await Promise.all(
        vinculos
          .filter((v: any) => v.statusRegistroAtivo === 'S')
          .map(async (v: any) => {
            const sesId = getSesIdFromRelacao(v);
            let detalhes: Ses | null = null;
            if (typeof sesId === 'number')
              detalhes = await getSesDetalhes(sesId);

            return {
              id: v.id,

              statusInterface: v.stInterface ?? v.statusInterface ?? 'N',
              tpVinculo: parseInt(String(v.tpVinculo), 10) || 2,
              ses: {
                id: detalhes?.id ?? sesId ?? undefined,
                nome: detalhes?.nome ?? v?.nome ?? '---',
                cnes: detalhes?.cnes ?? v?.cnes ?? '---',
                nomeEmpresarial:
                  detalhes?.nomeEmpresarial ?? v?.nomeEmpresarial ?? '---',
              },
              sesDetalhado: detalhes ?? undefined,
              dataVinculacao: v.dataVinculacao || null,
              usuarioVinculacao: v.usuarioVinculacao || null,
              observacao: v.observacao || '---',
              stRegistroAtivo: v.statusRegistroAtivo || 'N',
            };
          }),
      );

      setSesVinculadas(relacoesCompletas);
    } catch (error) {
      console.error('Erro ao buscar SES vinculados:', error);
    }
  };

  useEffect(() => {
    carregarVinculosAtivos();
  }, [userID]);

  const headersSes = [
    { text: '', width: 2, key: '' },
    { text: 'ID', width: 2, key: 'id' },
    { text: 'Nome do Estabelecimento', key: 'nome', width: 24 },
    { text: 'CNES', key: 'cnes', width: 12 },
    { text: 'Nome Empresarial', key: 'nomeEmpresarial', width: 24 },
    { text: 'Data de Vínculo', key: 'dataVinculo', width: 14 },
    { text: 'Responsável pelo Vínculo', key: 'usuarioVinculacao', width: 20 },
    { text: 'Tipo de Vínculo', key: 'tpVinculo', width: 14 },
    { text: 'Status', key: 'status', width: 10 },
  ];

  const tableDataSes = useMemo(() => {
    return sesVinculadas
      .filter((item) => item.stRegistroAtivo?.toUpperCase().trim() === 'S')
      .map((item) => ({
        id: item.id,
        nome: item.ses.nome,
        cnes: formatarCNES(item.ses.cnes),
        nomeEmpresarial: item.ses.nomeEmpresarial,
        dataVinculo: item.dataVinculacao
          ? formatDate(item.dataVinculacao)
          : '---',
        usuarioVinculacao:
          formataNomeMeioSiglas(item.usuarioVinculacao?.nomeCompleto || '') ||
          '---',
        tpVinculo:
          item.tpVinculo === 1
            ? 'Representante Técnico'
            : item.tpVinculo === 2
              ? 'Usuário'
              : '---',
        status: (
          <Status
            label={item.statusInterface === 'S' ? 'Ativo' : 'Pendente'}
            color={
              item.statusInterface === 'S'
                ? 'rgba(9, 175, 9, 0.655)'
                : 'rgba(200, 50, 50, 0.7)'
            }
            icon={
              item.statusInterface === 'S'
                ? FaCheckCircle
                : FaExclamationTriangle
            }
          />
        ),
      }));
  }, [sesVinculadas]);

  const handleDesvincularSes = async (id: number) => {
    try {
      setDesvinculando(true);

      await httpClient.delete(`/api/usuariointerface/${id}`);

      swalAtualizacao('Sucesso', 'Estabelecimento desvinculado com sucesso.');
      setShowDetalhesSes(false);

      await carregarVinculosAtivos();
    } catch (error) {
      console.error('Erro ao desvincular SES:', error);
      swal('error', 'Erro ao desvincular', 'Tente novamente mais tarde.');
    } finally {
      setDesvinculando(false);
    }
  };

  const handleConfirmarVinculo = async (id: number) => {
    try {
      if (!sesSelecionada) throw new Error('Nenhuma SES selecionada');

      const agoraISO = new Date().toISOString();

      const payload: Record<string, any> = {
        coUsuario: Number(user.sub),
        coUsuarioVinculacao: Number(user.sub),
        tpVinculo: sesSelecionada.tpVinculo ?? 2,

        stInterface: 'S',
        stRegistroAtivo: 'S',
        dsObservacao: 'Vínculo confirmado pelo usuário',
        dtVinculacao: sesSelecionada.dataVinculacao ?? agoraISO,
      };

      Object.keys(payload).forEach(
        (k) => payload[k] === undefined && delete payload[k],
      );

      await httpClient.patch(`/api/usuariointerface/${id}`, payload);

      setSesVinculadas((prev) =>
        prev.map((r) =>
          String(r.id) === String(id)
            ? {
                ...r,
                statusInterface: 'S',
                stRegistroAtivo: 'S',
                dataVinculacao: payload.dtVinculacao,
              }
            : r,
        ),
      );
      setSesSelecionada((prev) =>
        prev && String(prev.id) === String(id)
          ? {
              ...prev,
              statusInterface: 'S',
              stRegistroAtivo: 'S',
              dataVinculacao: payload.dtVinculacao,
            }
          : prev,
      );

      swalAtualizacao('Sucesso', 'Vínculo confirmado com sucesso.');
      setShowDetalhesSes(false);
    } catch (error: any) {
      console.error('Erro ao confirmar vínculo:', error);
      if (error?.response) {
        console.error('Status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      swal(
        'error',
        'Erro ao confirmar vínculo',
        error?.message ?? 'Tente novamente mais tarde.',
      );
    }
  };

  const [statusStyleUsuario, setStatusStyleUsuario] =
    useState<React.CSSProperties>({});
  useEffect(() => {
    const verde = '#abf3aba7';
    const vermelho = '#ffcecec3';
    setStatusStyleUsuario({
      backgroundColor: formData.stRegistroAtivo === 'S' ? verde : vermelho,
    });
  }, [formData.stRegistroAtivo]);

  return (
    <>
      <BarraSubAbas subAbas={SubAbas} />

      <ContainerConteudo>
        <WorkspaceFicha>
          <Modal
            icone="key"
            tituloModal="Alterar Senha"
            open={showModal}
            onHide={closeSenhaModal}
            modalSize="medio"
          >
            <div className="d-flex form flex-column justify-content-center align-items-center gap-4 m-3">
              <Input
                name="senha"
                id="senha"
                value={senha}
                label="Nova Senha"
                onChange={handleSenhaChange}
                onBlur={validaSenhaTamanho}
                type="password"
              />
              <Input
                name="confirmaSenha"
                value={confirmaSenha}
                label="Confirma Senha"
                id="confirmaSenha"
                onChange={handleConfirmaSenhaChange}
                type="password"
                onBlur={validaSenhaConfirma}
              />
              <WorkspaceBotoes
                cancelar
                onClickCancelar={closeSenhaModal}
                save
                onClickSave={updateSenha}
              />
            </div>
          </Modal>

          <BoxConteudo boxTitulo="Dados do Usuário">
            <CustomRow style={{ marginTop: '-15px' }}>
              <CustomCol width="12%">
                <SelectInput
                  name="stRegistroAtivo"
                  options={[
                    { label: 'Inativo', value: '0' },
                    { label: 'Ativo', value: '1' },
                  ]}
                  label="Status"
                  value={formData.stRegistroAtivo === 'S' ? '1' : '0'}
                  onChange={handleChange}
                  style={statusStyleUsuario}
                  disabled
                />
              </CustomCol>
              <CustomCol width="13%">
                <Input
                  name="cpf"
                  mascara="cpf"
                  value={formData.cpf ?? ''}
                  id="cpf"
                  label="CPF"
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
              <CustomCol width="32%">
                <Input
                  name="nomeCompleto"
                  value={formData.nomeCompleto ?? ''}
                  id="nome"
                  label="Nome completo"
                  onChange={handleChange}
                />
              </CustomCol>
              <CustomCol>
                <Input
                  name="dataNascimento"
                  type="date"
                  value={formData.dataNascimento ?? ''}
                  id="dataNascimento"
                  label="Data de nascimento"
                  onChange={handleChange}
                />
              </CustomCol>
              <CustomCol>
                <Select
                  name="genero"
                  value={formData.genero != null ? String(formData.genero) : ''}
                  onChange={handleChange}
                  options={generos}
                  label="Selecione o gênero"
                />
              </CustomCol>
              <CustomCol>
                <Select
                  name="cor"
                  value={formData.cor != null ? String(formData.cor) : ''}
                  onChange={handleChange}
                  options={cores}
                  label="Cor da pele"
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol width="13%">
                <Input
                  name="celular"
                  mascara="celular"
                  value={formData.celular ?? ''}
                  id="celular"
                  label="Celular"
                  onChange={handleChange}
                />
              </CustomCol>
              <CustomCol>
                <Input
                  name="emailPessoal"
                  value={formData.emailPessoal ?? ''}
                  id="emailPessoal"
                  label="Email pessoal"
                  onChange={handleChange}
                />
              </CustomCol>
              <CustomCol>
                <Input
                  name="linkedin"
                  value={formData.linkedin ?? ''}
                  id="linkedin"
                  label="LinkedIn"
                  onChange={handleChange}
                />
              </CustomCol>
              <CustomCol>
                <Input
                  name="lattes"
                  value={formData.lattes ?? ''}
                  id="lattes"
                  label="Lattes"
                  onChange={handleChange}
                />
              </CustomCol>
            </CustomRow>
          </BoxConteudo>

          <BoxConteudo boxTitulo="Estabelecimento de Saúde Vinculados">
            <Tabela
              headers={headersSes}
              dataTable={tableDataSes}
              rowClickable={true}
              showPagination={false}
              onRowClick={(payload: any) => {
                const clickedId =
                  payload && typeof payload === 'object'
                    ? 'id' in payload
                      ? (payload as any).id
                      : ((payload as any)?.original?.id ??
                        (payload as any)?.row?.id ??
                        (payload as any)?.values?.id)
                    : payload;

                let ses: VinculoSes | undefined;
                if (clickedId != null) {
                  ses = sesVinculadas.find(
                    (item) => String(item.id) === String(clickedId),
                  );
                }

                if (!ses && Number.isInteger(clickedId)) {
                  ses = sesVinculadas[Number(clickedId)];
                }
                if (!ses) {
                  console.warn(
                    '[Tabela.onRowClick] SES não localizada para payload:',
                    payload,
                  );
                  return;
                }
                setSesSelecionada(ses);
                setShowDetalhesSes(true);
              }}
            />
          </BoxConteudo>
        </WorkspaceFicha>
      </ContainerConteudo>

      {showDetalhesSes && sesSelecionada && (
        <Modal
          tituloModal="Detalhes do Vínculo"
          open={showDetalhesSes}
          onHide={() => setShowDetalhesSes(false)}
        >
          <WorkspaceFicha>
            <Log dataRegistro={sesSelecionada.dataVinculacao || undefined} />

            <CustomRow>
              <CustomCol width="70%">
                <Input
                  id="nome-estabelecimento"
                  label="Nome do Estabelecimento *"
                  value={
                    sesSelecionada.sesDetalhado?.nome ??
                    sesSelecionada.ses.nome ??
                    '---'
                  }
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
              <CustomCol width="30%">
                <Input
                  id="cnes"
                  label="CNES *"
                  value={formatarCNES(
                    sesSelecionada.sesDetalhado?.cnes ??
                      sesSelecionada.ses.cnes,
                  )}
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
            </CustomRow>

            <CustomRow>
              <CustomCol width="100%">
                <Input
                  id="nome-empresarial"
                  label="Nome Empresarial"
                  value={
                    sesSelecionada.sesDetalhado?.nomeEmpresarial ??
                    sesSelecionada.ses.nomeEmpresarial ??
                    '---'
                  }
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
            </CustomRow>

            <CustomRow>
              <CustomCol width="50%">
                <Input
                  id="data-vinculacao"
                  label="Data de Vinculação"
                  value={
                    sesSelecionada.dataVinculacao
                      ? formatDate(sesSelecionada.dataVinculacao)
                      : '---'
                  }
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
              <CustomCol width="50%">
                <Input
                  id="status-vinculacao"
                  label="Status"
                  value={
                    sesSelecionada.statusInterface === 'S'
                      ? 'Ativo'
                      : 'Pendente'
                  }
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
            </CustomRow>

            <CustomRow>
              <CustomCol width="50%">
                <Input
                  id="tipo-vinculo"
                  label="Tipo de Vínculo"
                  value={
                    sesSelecionada.tpVinculo === 1
                      ? 'Representante Técnico'
                      : sesSelecionada.tpVinculo === 2
                        ? 'Usuário'
                        : '---'
                  }
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
              <CustomCol width="50%">
                <Input
                  id="responsavel-vinculacao"
                  label="Responsável pelo Vínculo"
                  value={
                    formataNomeMeioSiglas(
                      sesSelecionada.usuarioVinculacao?.nomeCompleto || '',
                    ) || '---'
                  }
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
            </CustomRow>

            <CustomRow>
              <Col>
                <Input
                  id="observacoes-vinculacao"
                  label="Observações"
                  value={sesSelecionada.observacao || '---'}
                  type="textarea"
                  onChange={handleChange}
                  readonly
                />
              </Col>
            </CustomRow>

            <BarraInferiorBotoes>
              {sesSelecionada.statusInterface === 'S' ? (
                <WorkspaceBotoesNew
                  label="Desvincular"
                  tipo="cancel"
                  icone={<FaTrash />}
                  onClick={() =>
                    handleDesvincularSes(Number(sesSelecionada.id))
                  }
                  style={{ marginRight: '8px' }}
                />
              ) : (
                <WorkspaceBotoesNew
                  label="Confirmar"
                  tipo="save"
                  icone={<FaCheckCircle />}
                  onClick={() =>
                    handleConfirmarVinculo(Number(sesSelecionada.id))
                  }
                  style={{ marginRight: '8px' }}
                />
              )}
            </BarraInferiorBotoes>
          </WorkspaceFicha>
        </Modal>
      )}

      <BarraInferiorBotoes>
        <WorkspaceBotoes
          save
          onClickSave={updateData}
          senha
          onClickSenha={openSenhaModal}
        />
      </BarraInferiorBotoes>
    </>
  );
};

export default MeusDadosUsuario;
